import React from "react";
import { Grid, Typography, Button, Box, Chip } from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VerifiedIcon from "@mui/icons-material/Verified";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import derivImage from "../../assets/images/partners/deriv.png";

// ============================================================
// COLOR PALETTE (Light Blue + Deep Blue only)
// ============================================================

const DEEP_BLUE = "#0a1f44";
const PRIMARY_BLUE = "#0a4fd5";
const LIGHT_BLUE = "#4a9eff";
const SOFT_BLUE = "#e8f2ff";
const BORDER_BLUE = "#d4e5ff";
const TEXT_MUTED = "#5a6b85";

// ============================================================
// ANIMATIONS
// ============================================================

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-8px);
  }
  100% {
    transform: translateY(0px);
  }
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(10, 79, 213, 0.4);
  }
  70% {
    box-shadow: 0 0 0 15px rgba(10, 79, 213, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(10, 79, 213, 0);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
`;

// ============================================================
// STYLED COMPONENTS
// ============================================================

const SectionWrapper = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "60px 20px",
  animation: `${fadeInUp} 0.8s ease forwards`,
});

const BrokerCard = styled(Box)({
  backgroundColor: "#ffffff",
  borderRadius: "20px",
  padding: "28px",
  boxShadow: "0 10px 40px rgba(10, 79, 213, 0.08)",
  border: `1px solid ${BORDER_BLUE}`,
  position: "relative",
  overflow: "hidden",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg, ${DEEP_BLUE}, ${PRIMARY_BLUE}, ${LIGHT_BLUE})`,
    backgroundSize: "200% auto",
    animation: `${shimmer} 3s linear infinite`,
  },

  "&:hover": {
    transform: "translateY(-6px)",
    boxShadow: "0 20px 60px rgba(10, 79, 213, 0.2)",
    borderColor: PRIMARY_BLUE,
  },
});

const LogoContainer = styled(Box)({
  padding: "24px",
  borderRadius: "14px",
  backgroundColor: SOFT_BLUE,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: "24px",
  border: `1px solid ${BORDER_BLUE}`,
  animation: `${float} 3s ease-in-out infinite`,
  transition: "all 0.4s ease",

  "&:hover": {
    backgroundColor: "#dbeaff",
    borderColor: PRIMARY_BLUE,
    animationPlayState: "paused",
  },
});

const TrustBadge = styled(Chip)({
  backgroundColor: SOFT_BLUE,
  color: PRIMARY_BLUE,
  fontWeight: 700,
  fontSize: "12px",
  border: `1px solid ${BORDER_BLUE}`,
  animation: `${pulse} 2.5s infinite`,

  "& .MuiChip-icon": {
    color: PRIMARY_BLUE,
  },
});

// ============================================================
// CHOOSE BROKER BUTTON
// MATCHES HEADER: black → #000080 on hover
// ============================================================

const ChooseButton = styled(Button)({
  backgroundColor: "#000000",          // BLACK by default (matches header)
  color: "#ffffff",
  padding: "14px 32px",
  borderRadius: "8px",
  fontWeight: 600,
  fontSize: "13px",
  letterSpacing: "0.5px",
  textTransform: "none",
  width: "100%",
  border: "2px solid transparent",
  cursor: "pointer",
  transition: "all 0.3s ease",

  "&:hover": {
    backgroundColor: "#000080",        // DEEP NAVY on hover (matches header)
    color: "#ffffff",
    borderColor: "#000080",
    transform: "translateY(-2px)",
    boxShadow: "0 4px 15px rgba(0, 0, 128, 0.3)",
  },

  "&:active": {
    transform: "translateY(0) scale(0.98)",
  },
});

const FeatureItem = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "10px 0",
  transition: "all 0.3s ease",

  "&:hover": {
    transform: "translateX(6px)",

    "& .MuiSvgIcon-root": {
      color: PRIMARY_BLUE,
    },
  },
});

// ============================================================
// MAIN COMPONENT
// ============================================================

const BrokerSection = () => {
  const broker = {
    name: "Deriv",
    tagline: "Regulated FX/CFD Broker",
    description:
      "Deriv is a leading trading platform offering a wide range of assets and CFDs, with user-friendly UI and superior execution.",
    features: [
      "Regulated & Trusted Platform",
      "Wide Range of Assets & CFDs",
      "User-Friendly Interface",
      "Fast & Reliable Execution",
      "Low Minimum Deposit",
    ],
  };

  const handleChooseBroker = () => {
    window.open("https://deriv.com", "_blank", "noopener,noreferrer");
  };

  return (
    <SectionWrapper>
      <Box
        sx={{
          width: { xs: "95%", sm: "90%", md: "1100px" },
        }}
      >
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
          {/* ==================================================
              LEFT SECTION - TEXT CONTENT
          ================================================== */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                animation: `${fadeInUp} 1s ease forwards`,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: PRIMARY_BLUE,
                  fontWeight: 700,
                  fontSize: "14px",
                  letterSpacing: "2px",
                  textTransform: "uppercase",
                  mb: 1.5,
                }}
              >
                Or
              </Typography>

              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  color: DEEP_BLUE,
                  lineHeight: 1.25,
                  fontSize: { xs: "1.7rem", sm: "2rem", md: "2.4rem" },
                  mb: 2.5,
                }}
              >
                Become A Professional{" "}
                <Box
                  component="span"
                  sx={{
                    color: PRIMARY_BLUE,
                    position: "relative",
                    display: "inline-block",
                  }}
                >
                  Forex Trader!
                </Box>
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: TEXT_MUTED,
                  lineHeight: 1.8,
                  fontSize: "15.5px",
                  mb: 3,
                }}
              >
                Get{" "}
                <strong style={{ color: PRIMARY_BLUE }}>LIFETIME</strong>{" "}
                access to our VIP forex signals for{" "}
                <strong style={{ color: LIGHT_BLUE }}>FREE!</strong> Register
                and deposit the minimum <strong>250 USD</strong> with our
                chosen regulated FX/CFD broker below and receive LIFETIME
                access to our VIP Forex Signals for FREE!
              </Typography>

              {/* Feature Highlights */}
              <Box sx={{ mt: 3 }}>
                {[
                  "Instant access to VIP signals",
                  "Expert trader mentorship",
                  "Exclusive market analysis",
                ].map((feature, index) => (
                  <FeatureItem key={index}>
                    <CheckCircleIcon
                      sx={{
                        color: PRIMARY_BLUE,
                        fontSize: 20,
                        transition: "color 0.3s ease",
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: DEEP_BLUE,
                        fontWeight: 500,
                        fontSize: "14.5px",
                      }}
                    >
                      {feature}
                    </Typography>
                  </FeatureItem>
                ))}
              </Box>
            </Box>
          </Grid>

          {/* ==================================================
              RIGHT SECTION - BROKER CARD
          ================================================== */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                animation: `${fadeInUp} 1s ease 0.2s forwards`,
                opacity: 0,
              }}
            >
              <BrokerCard>
                {/* Trust Badge */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <TrustBadge
                    icon={<VerifiedIcon sx={{ fontSize: 16 }} />}
                    label="Verified Partner"
                    size="small"
                  />
                </Box>

                {/* Deriv Logo */}
                <LogoContainer>
                  <Box
                    component="img"
                    src={derivImage}
                    alt="Deriv logo"
                    sx={{
                      width: { xs: "70%", sm: "60%" },
                      maxWidth: "220px",
                      height: "auto",
                      objectFit: "contain",
                      transition: "all 0.4s ease",
                    }}
                  />
                </LogoContainer>

                {/* Broker Name & Tagline */}
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: DEEP_BLUE,
                    mb: 0.5,
                    textAlign: "center",
                  }}
                >
                  {broker.name}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: PRIMARY_BLUE,
                    fontWeight: 600,
                    fontSize: "13px",
                    textAlign: "center",
                    mb: 2,
                  }}
                >
                  {broker.tagline}
                </Typography>

                {/* Description */}
                <Typography
                  variant="body2"
                  sx={{
                    color: TEXT_MUTED,
                    lineHeight: 1.7,
                    fontSize: "14px",
                    textAlign: "center",
                    mb: 2.5,
                  }}
                >
                  {broker.description}
                </Typography>

                {/* Feature List */}
                <Box
                  sx={{
                    backgroundColor: SOFT_BLUE,
                    borderRadius: "12px",
                    padding: "16px 20px",
                    mb: 3,
                    border: `1px solid ${BORDER_BLUE}`,
                  }}
                >
                  {broker.features.map((feature, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        py: 0.6,
                      }}
                    >
                      <CheckCircleIcon
                        sx={{
                          color: PRIMARY_BLUE,
                          fontSize: 16,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: DEEP_BLUE,
                          fontWeight: 500,
                          fontSize: "13.5px",
                        }}
                      >
                        {feature}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Choose Broker Button */}
                <ChooseButton
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={handleChooseBroker}
                >
                  Choose Deriv Broker
                </ChooseButton>

                {/* Disclaimer */}
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    color: TEXT_MUTED,
                    mt: 2.5,
                    lineHeight: 1.6,
                    fontSize: "11.5px",
                    textAlign: "center",
                  }}
                >
                  Don't invest unless you're prepared to lose all the money
                  you invest. This is a high-risk investment, and you are
                  unlikely to be protected if something goes wrong.{" "}
                  <a
                    href="/learn-more"
                    style={{
                      color: PRIMARY_BLUE,
                      textDecoration: "none",
                      fontWeight: 600,
                    }}
                  >
                    Take 2 minutes to learn more
                  </a>
                  .
                </Typography>
              </BrokerCard>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </SectionWrapper>
  );
};

export default BrokerSection;